import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Inbox, ChevronDown, ChevronRight } from 'lucide-react';
import { PageLayout } from '../../../components/ui/PageLayout';
import Button from '../../../components/ui/Button';
import { Spinner } from '../../../components/ui/Spinner';
import { useToast, ToastRenderer } from '../../../hooks/useToast';
import { getApiErrorMessage } from '../../../utils/apiErrors';
import { parseApiUtc } from '@/utils/apiDate';
import { appDateTime } from '@/utils/appDateFormat';
import {
  getMarketingLeads,
  MarketingLeadSource,
  MarketingLeadView,
} from '../../../api/Marketing/MarketingLeads';

/*
  Operator-only list of leads captured by the public marketing website (sandbox gate, contact form,
  sales assistant). Guarded by OPERATOR_ROLES in App.tsx; the server enforces Policy = "Operator" on
  GET /api/MarketingLeads, which is what actually keeps tenant roles out.
  Strings live in the `marketing` namespace (public/locales/{en,ar,fr}/marketing.json).
*/

const PAGE_SIZE = 100;
const EXPORT_PAGE_SIZE = 500;

const SOURCE_FILTERS: Array<{ value: MarketingLeadSource | ''; labelKey: string }> = [
  { value: '', labelKey: 'leads.filter.all' },
  { value: 'sandbox-gate', labelKey: 'leads.source.sandbox-gate' },
  { value: 'chatbot', labelKey: 'leads.source.chatbot' },
  { value: 'contact', labelKey: 'leads.source.contact' },
];

const INTEREST_KEYS: Record<string, string> = {
  connect: 'leads.interest.connect',
  hrms: 'leads.interest.hrms',
  engageiq: 'leads.interest.engageiq',
};

/** One-line summary for the table: the message, else the first visitor line of the transcript. */
const summaryOf = (lead: MarketingLeadView): string => {
  if (lead.message?.trim()) return lead.message.trim();
  const firstVisitor = lead.transcript?.split('\n').find(l => l.startsWith('Visitor: '));
  return firstVisitor ? firstVisitor.slice('Visitor: '.length) : '';
};

/** CSV cell: quoted, and neutralised against spreadsheet formula injection (visitor-typed text). */
const csvCell = (value: unknown): string => {
  let s = value == null ? '' : String(value);
  if (/^[=+\-@\t\r]/.test(s)) s = `'${s}`;
  return `"${s.replace(/"/g, '""')}"`;
};

export default function MarketingLeadsIndex() {
  const { t } = useTranslation('marketing');
  const { toast, showToast, dismissToast } = useToast();

  const [source, setSource] = useState<MarketingLeadSource | ''>('');
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(false);
  const [rows, setRows] = useState<MarketingLeadView[]>([]);
  const [total, setTotal] = useState(0);
  const [expanded, setExpanded] = useState<Set<number>>(new Set());

  const sourceLabel = (value: string) =>
    ['sandbox-gate', 'chatbot', 'contact'].includes(value) ? t(`leads.source.${value}`) : value;

  const fetchLeads = useCallback(async () => {
    setLoading(true);
    try {
      const res = await getMarketingLeads({ source, page, pageSize: PAGE_SIZE });
      if (res.errors?.length) {
        showToast(getApiErrorMessage(res, t, t('leads.error.load')), 'error');
      } else {
        setRows(res.payload?.items ?? []);
        setTotal(res.payload?.totalCount ?? 0);
      }
    } catch (e) {
      console.error(e);
      showToast(getApiErrorMessage(e, t, t('leads.error.load')), 'error');
    } finally {
      setLoading(false);
    }
    // showToast is stable per useToast; t changes with language only.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [source, page]);

  useEffect(() => {
    fetchLeads();
  }, [fetchLeads]);

  const toggle = (id: number) =>
    setExpanded(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  const handleExport = async () => {
    setExporting(true);
    try {
      const all: MarketingLeadView[] = [];
      for (let p = 1; ; p++) {
        const res = await getMarketingLeads({ source, page: p, pageSize: EXPORT_PAGE_SIZE });
        if (res.errors?.length) {
          showToast(getApiErrorMessage(res, t, t('leads.error.export')), 'error');
          return;
        }
        const items = res.payload?.items ?? [];
        all.push(...items);
        if (items.length < EXPORT_PAGE_SIZE || all.length >= (res.payload?.totalCount ?? 0)) break;
      }

      const header = [
        'Received (UTC)', 'Source', 'Name', 'Company', 'Company size', 'Country', 'Job title', 'Email',
        'Phone', 'Interests', 'Message', 'Locale', 'Page', 'UTM source', 'UTM medium', 'UTM campaign',
        'Transcript', 'Sales notified (UTC)',
      ];
      const lines = all.map(l => [
        l.createdUtc, l.source, l.name, l.companyName, l.companySize, l.country, l.jobTitle, l.email,
        l.phone, l.interests.join(' '), l.message, l.locale, l.pageUrl, l.utmSource, l.utmMedium,
        l.utmCampaign, l.transcript, l.salesNotifiedUtc,
      ].map(csvCell).join(','));
      // BOM so Excel opens Arabic names correctly.
      const csv = '﻿' + [header.map(csvCell).join(','), ...lines].join('\r\n');
      const url = URL.createObjectURL(new Blob([csv], { type: 'text/csv;charset=utf-8' }));
      const a = document.createElement('a');
      a.href = url;
      a.download = `marketing-leads-${new Date().toISOString().slice(0, 10)}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (e) {
      console.error(e);
      showToast(getApiErrorMessage(e, t, t('leads.error.export')), 'error');
    } finally {
      setExporting(false);
    }
  };

  const pageCount = Math.max(1, Math.ceil(total / PAGE_SIZE));

  return (
    <PageLayout
      title={t('leads.title')}
      description={t('leads.description')}
      icon={Inbox}
      accentColor="slate"
      maxWidth="1600px"
      headerActions={
        <Button
          variant="outline"
          size="sm"
          onClick={handleExport}
          loading={exporting}
          disabled={exporting || total === 0}
          iconName="Download"
        >
          {t('leads.export')}
        </Button>
      }
    >
      <div className="flex flex-col gap-4">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div role="radiogroup" aria-label={t('leads.filter.label')} className="inline-flex flex-wrap gap-1 rounded-lg border border-border bg-muted/40 p-1">
            {SOURCE_FILTERS.map(f => {
              const active = source === f.value;
              return (
                <button
                  key={f.value || 'all'}
                  type="button"
                  role="radio"
                  aria-checked={active}
                  onClick={() => { setSource(f.value); setPage(1); setExpanded(new Set()); }}
                  className={`rounded-md px-3 py-1.5 text-sm font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/30 ${
                    active ? 'bg-card text-foreground shadow-sm' : 'text-muted-foreground hover:text-foreground'
                  }`}
                >
                  {t(f.labelKey)}
                </button>
              );
            })}
          </div>
          {!loading && (
            <p className="text-sm text-muted-foreground" aria-live="polite">
              {t('leads.count', { count: total })}
            </p>
          )}
        </div>

        {loading ? (
          <div className="flex justify-center py-16">
            <Spinner size="lg" />
          </div>
        ) : rows.length === 0 ? (
          <div className="rounded-xl border border-border bg-card p-10 text-center">
            <p className="text-sm font-medium text-foreground">{t('leads.empty.title')}</p>
            <p className="mt-1 text-sm text-muted-foreground">{t('leads.empty.body')}</p>
          </div>
        ) : (
          <div className="overflow-x-auto rounded-xl border border-border bg-card">
            <table className="w-full min-w-[1100px] text-sm">
              <thead className="text-xs uppercase text-muted-foreground">
                <tr className="border-b border-border">
                  <th className="w-8 py-2.5 ps-3" aria-label={t('leads.col.details')} />
                  <th className="py-2.5 px-3 text-start font-medium">{t('leads.col.date')}</th>
                  <th className="py-2.5 px-3 text-start font-medium">{t('leads.col.source')}</th>
                  <th className="py-2.5 px-3 text-start font-medium">{t('leads.col.name')}</th>
                  <th className="py-2.5 px-3 text-start font-medium">{t('leads.col.company')}</th>
                  <th className="py-2.5 px-3 text-start font-medium">{t('leads.col.size')}</th>
                  <th className="py-2.5 px-3 text-start font-medium">{t('leads.col.country')}</th>
                  <th className="py-2.5 px-3 text-start font-medium">{t('leads.col.email')}</th>
                  <th className="py-2.5 px-3 text-start font-medium">{t('leads.col.phone')}</th>
                  <th className="py-2.5 px-3 text-start font-medium">{t('leads.col.interests')}</th>
                  <th className="py-2.5 px-3 text-start font-medium">{t('leads.col.summary')}</th>
                </tr>
              </thead>
              <tbody>
                {rows.map(lead => {
                  const isOpen = expanded.has(lead.id);
                  const summary = summaryOf(lead);
                  const utm = [lead.utmSource, lead.utmMedium, lead.utmCampaign].filter(Boolean).join(' / ');
                  return (
                    <React.Fragment key={lead.id}>
                      <tr className="border-b border-border last:border-b-0 align-top hover:bg-muted/30">
                        <td className="py-2.5 ps-3">
                          <button
                            type="button"
                            onClick={() => toggle(lead.id)}
                            aria-expanded={isOpen}
                            aria-controls={`lead-details-${lead.id}`}
                            aria-label={isOpen ? t('leads.collapse') : t('leads.expand')}
                            className="rounded p-0.5 text-muted-foreground hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/30"
                          >
                            {isOpen
                              ? <ChevronDown className="h-4 w-4" aria-hidden="true" />
                              : <ChevronRight className="h-4 w-4 rtl:rotate-180" aria-hidden="true" />}
                          </button>
                        </td>
                        <td className="whitespace-nowrap py-2.5 px-3 tabular-nums text-muted-foreground">
                          {appDateTime(parseApiUtc(lead.createdUtc))}
                        </td>
                        <td className="whitespace-nowrap py-2.5 px-3">
                          <span className="rounded-md border border-border bg-muted/50 px-2 py-0.5 text-xs font-medium text-foreground">
                            {sourceLabel(lead.source)}
                          </span>
                        </td>
                        <td className="py-2.5 px-3 font-medium text-foreground">
                          {lead.name}
                          {lead.jobTitle && <div className="text-xs font-normal text-muted-foreground">{lead.jobTitle}</div>}
                        </td>
                        <td className="py-2.5 px-3 text-foreground">{lead.companyName}</td>
                        <td className="whitespace-nowrap py-2.5 px-3 text-muted-foreground">{lead.companySize || '—'}</td>
                        <td className="py-2.5 px-3 text-muted-foreground">{lead.country || '—'}</td>
                        <td className="py-2.5 px-3">
                          <a href={`mailto:${lead.email}`} className="text-primary hover:underline" dir="ltr">{lead.email}</a>
                        </td>
                        <td className="whitespace-nowrap py-2.5 px-3 text-muted-foreground" dir="ltr">
                          {lead.phone ? <a href={`tel:${lead.phone}`} className="hover:underline">{lead.phone}</a> : '—'}
                        </td>
                        <td className="py-2.5 px-3">
                          {lead.interests.length === 0 ? (
                            <span className="text-muted-foreground">—</span>
                          ) : (
                            <div className="flex flex-wrap gap-1">
                              {lead.interests.map(i => (
                                <span key={i} className="whitespace-nowrap rounded-md bg-primary/10 px-1.5 py-0.5 text-xs font-medium text-primary">
                                  {INTEREST_KEYS[i] ? t(INTEREST_KEYS[i]) : i}
                                </span>
                              ))}
                            </div>
                          )}
                        </td>
                        <td className="max-w-xs py-2.5 px-3 text-muted-foreground">
                          <span className="line-clamp-2">{summary || '—'}</span>
                        </td>
                      </tr>
                      {isOpen && (
                        <tr id={`lead-details-${lead.id}`} className="border-b border-border bg-muted/20 last:border-b-0">
                          <td />
                          <td colSpan={10} className="py-4 px-3">
                            <dl className="grid grid-cols-1 gap-x-8 gap-y-3 text-sm md:grid-cols-3">
                              <div>
                                <dt className="text-xs text-muted-foreground">{t('leads.detail.locale')}</dt>
                                <dd className="text-foreground">{lead.locale}</dd>
                              </div>
                              <div>
                                <dt className="text-xs text-muted-foreground">{t('leads.detail.page')}</dt>
                                <dd className="break-all text-foreground" dir="ltr">{lead.pageUrl || '—'}</dd>
                              </div>
                              <div>
                                <dt className="text-xs text-muted-foreground">{t('leads.detail.utm')}</dt>
                                <dd className="text-foreground" dir="ltr">{utm || '—'}</dd>
                              </div>
                              <div>
                                <dt className="text-xs text-muted-foreground">{t('leads.detail.notified')}</dt>
                                <dd className="text-foreground">
                                  {lead.salesNotifiedUtc ? appDateTime(parseApiUtc(lead.salesNotifiedUtc)) : t('leads.detail.notNotified')}
                                </dd>
                              </div>
                            </dl>
                            {lead.message && (
                              <div className="mt-4">
                                <h4 className="text-xs font-medium text-muted-foreground">{t('leads.detail.message')}</h4>
                                <p className="mt-1 max-w-[75ch] whitespace-pre-wrap text-foreground">{lead.message}</p>
                              </div>
                            )}
                            {lead.transcript && (
                              <div className="mt-4">
                                <h4 className="text-xs font-medium text-muted-foreground">{t('leads.detail.transcript')}</h4>
                                <pre className="mt-1 max-h-80 max-w-[90ch] overflow-auto whitespace-pre-wrap rounded-lg border border-border bg-background p-3 font-sans text-sm text-foreground">
                                  {lead.transcript}
                                </pre>
                              </div>
                            )}
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}

        {!loading && pageCount > 1 && (
          <div className="flex items-center justify-end gap-2">
            <Button variant="outline" size="sm" disabled={page <= 1} onClick={() => setPage(p => Math.max(1, p - 1))}>
              {t('leads.prev')}
            </Button>
            <span className="text-sm tabular-nums text-muted-foreground">
              {t('leads.pageOf', { page, pages: pageCount })}
            </span>
            <Button variant="outline" size="sm" disabled={page >= pageCount} onClick={() => setPage(p => p + 1)}>
              {t('leads.next')}
            </Button>
          </div>
        )}
      </div>

      <ToastRenderer toast={toast} onDismiss={dismissToast} />
    </PageLayout>
  );
}
